/** Your top sites: where your links pointed, each with a bar against the top one. */

import type { CSSProperties } from "react";

import { Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "blurple", x: 1010, y: 1800, r: 280, seed: 24.1, wob: 0.18 },
  { colour: "fuchsia", x: 1050, y: 250, r: 110, seed: 25.3, wob: 0.24 },
];

const ROW = 180;
const CLIP: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

export function Sites({ sites }: { sites: You["sites"] }) {
  const shown = sites.slice(0, 5);
  const most = Math.max(...shown.map((s) => s.count), 1);
  // We only get your top sites, so the total is theirs, not every link you sent.
  const total = shown.reduce((sum, s) => sum + s.count, 0);

  const root = useTimeline((s) => {
    s.rise(".kick, .lead", 0.35, { y: 30, stagger: 0.12 });
    s.q(".site").forEach((row, i) => {
      s.rise(row, 0.6 + i * 0.15, { y: 30, dur: 0.5 });
      s.tl.fromTo(row.querySelector(".site-bar"), { scaleX: 0 }, { scaleX: 1, duration: 0.7, ease: "power3.out" }, 0.75 + i * 0.15);
      s.sound("zip", 0.75 + i * 0.15, 1 + i * 0.1, { length: 0.7, pan: -0.4 });
    });
    s.rise(".s-total", 2.0, { y: 24 });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick" style={{ color: wr("yellow") }}>
            {shown.length === 1 ? "Your top site" : "Your top sites"}
          </div>
          <div className="lead">Where your links pointed</div>
        </Zone>
        <Zone at="middle">
          {shown.map((site, i) => (
            <div key={site.domain} className="site" style={{ top: 400 + i * ROW }}>
              <div className="site-h">
                <span className="rk">#{i + 1}</span>
                <b style={CLIP}>{site.domain}</b>
                <em>{count(site.count)}</em>
              </div>
              <div
                className="site-bar"
                style={{ width: Math.max(60, (site.count / most) * 936), background: i === 0 ? wr("yellow") : wr("white-82") }}
              />
            </div>
          ))}
        </Zone>
        <Zone at="bottom">
          <div className="line s-total" style={{ top: 400 + shown.length * ROW + 40 }}>
            <b>{count(total)}</b> {total === 1 ? "link" : "links"} to your top {shown.length === 1 ? "site" : "sites"}.
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
